// A plan in the words a patient reads — derived from the server's projection,
// one function per sentence, so the card and its test say the same thing.

import { APP_LOCALE } from "../../../utils/appLocale";
import type { PatientPlan, PlanItem } from "./types";

/**
 * "Aug 28, 2027". A plan's dates are the CLINIC's calendar days, sent as
 * "YYYY-MM-DD": read at noon UTC and written in UTC, so no browser zone can
 * move them a day.
 */
export function civilDateWords(civil: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(civil || "");
  if (!match) return null;
  const at = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  return new Date(at).toLocaleDateString(APP_LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function money(cents: number, currency = "CAD"): string {
  return new Intl.NumberFormat(APP_LOCALE, { style: "currency", currency }).format(Number(cents || 0) / 100);
}

const STATE_WORDS: Record<string, string> = {
  active: "Active",
  not_started: "Not started yet",
  used_up: "Used up",
  expired: "Expired",
  ended: "Ended",
  cancelled: "Ended",
};

export function stateWords(plan: PatientPlan): string {
  const words = STATE_WORDS[plan.state] || "Active";
  const on = civilDateWords(plan.stateOn);
  if (plan.state === "not_started" && on) return `Starts ${on}`;
  if ((plan.state === "expired" || plan.state === "ended" || plan.state === "cancelled") && on) return `${words} ${on}`;
  return words;
}

/** When the plan runs out, as a whole. */
export function validityWords(plan: PatientPlan): string {
  const { kind, on, durationMonths } = plan.validity || { kind: "none" };
  const date = civilDateWords(on);
  if (kind === "ends" && date) return `Valid until ${date}`;
  if (kind === "first_use")
    return durationMonths
      ? `Valid for ${durationMonths} month${durationMonths === 1 ? "" : "s"} from your first visit`
      : "Starts counting from your first visit";
  if (kind === "items") return date ? `Each item has its own dates — the last ends ${date}` : "Each item has its own dates";
  return "No expiry";
}

export function whereWords(plan: PatientPlan): string {
  if (plan.usableAt?.everywhere) return "Usable at every clinic";
  const places = plan.usableAt?.locations || [];
  if (places.length === 0) return "";
  return `Usable at ${places.join(", ")}`;
}

/** "2 of 3 left", "Unlimited", "15% off" — and the item's own end. */
export function itemWords(item: PlanItem): string {
  let what: string;
  if (item.discountPercent) what = `${item.discountPercent}% off`;
  else if (item.discountCents) what = `${money(item.discountCents)} off`;
  else if (item.unlimited) what = "Unlimited";
  else what = `${item.remaining ?? 0} of ${item.total ?? 0} left`;
  const until = civilDateWords(item.expiresOn);
  const opens = civilDateWords(item.opensOn);
  if (opens) return `${what} · from ${opens}`;
  return until ? `${what} · until ${until}` : what;
}

export function renewalWords(plan: PatientPlan): string | null {
  const renewal = plan.renewal;
  if (!renewal) return null;
  const ends = civilDateWords(renewal.endsOn);
  if (ends) return renewal.state === "ended" ? `Ended ${ends}` : `Ends ${ends} — it will not renew`;
  if (renewal.state === "paused") return "Paused — it does not renew until it is resumed";
  const on = civilDateWords(renewal.renewsOn);
  const amount =
    renewal.nextChargeCents != null
      ? ` · ${money(renewal.nextChargeCents, renewal.currency)}${renewal.interval ? ` / ${renewal.interval}` : ""}`
      : "";
  return on ? `Renews ${on}${amount}` : null;
}
